/** @jsx jsx */

/** @jsxFrag Fragment */
import { css, jsx } from '@emotion/react';

import { Fragment, useReducer } from 'react';

import { getUserLanguageCode, languageCodeToName } from 'js/lib/language';

import { Button, InlineNotification } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import {
  useGetNotificationType,
  useItemLanguageSwitcher,
} from 'bundles/course-notifications-v2/components/translatedContent/TranslationNotificationHooks';
import ErrorBoundaryWithLogging from 'bundles/page/components/ErrorBoundaryWithLogging';

import _t from 'i18n!nls/course-notifications-v2';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type Props = {
  courseId: string;
};

const TranslatedItemBannerInternal = ({ courseId }: Props) => {
  const userLocale = getUserLanguageCode();
  const notification = useGetNotificationType(courseId, userLocale);
  const { preferredLanguage, switchLanguage, savePreferredLanguage } = useItemLanguageSwitcher();
  const [isDismissed, dismiss] = useReducer(() => true, false);

  const trackingData = {
    courseId,
    userLocale,
    preferredLanguage,
  };

  if (isDismissed) return null;
  if (notification?.type === 'AUTO_TRANSLATED') {
    return (
      <InlineNotification
        severity="information"
        title={_t('You’re viewing an automatically-translated version of this assessment')}
        onDismiss={dismiss}
      >
        <>
          {_t(
            'You can switch back to view this content in its original language if you’d prefer. You won’t lose any progress if you change languages.'
          )}
          <TrackedButton
            trackingName="switch_to_original_language"
            trackingData={trackingData}
            css={css`
              padding: 0 var(--cds-spacing-100);
              margin-top: -4px;
            `}
            variant="ghost"
            onClick={() => {
              savePreferredLanguage(userLocale);
              switchLanguage(notification.originalLanguage);
            }}
          >
            {_t('Show #{language} version', { language: languageCodeToName(notification.originalLanguage) })}
          </TrackedButton>
        </>
      </InlineNotification>
    );
  }
  if (preferredLanguage) {
    return (
      <InlineNotification
        severity="information"
        title={_t('You’re viewing this assessment in its original language')}
        onDismiss={() => {
          // clear the preferred language so that it won't show next time
          savePreferredLanguage(null);
          dismiss();
        }}
      >
        <>
          {_t(
            'You can switch back to view this content in your preferred translation if you’d prefer. You won’t lose any progress if you change languages.'
          )}
          <TrackedButton
            trackingName="switch_to_preferred_language"
            trackingData={trackingData}
            css={css`
              padding: 0 var(--cds-spacing-100);
              margin-top: -2px;
            `}
            variant="ghost"
            onClick={() => {
              savePreferredLanguage(null);
              switchLanguage(preferredLanguage);
            }}
          >
            {_t('Show #{language} version', { language: languageCodeToName(preferredLanguage) })}
          </TrackedButton>
        </>
      </InlineNotification>
    );
  }
  return null;
};

const TranslatedItemBanner = (props: Props) => {
  return (
    <ErrorBoundaryWithLogging>
      <TranslatedItemBannerInternal {...props} />
    </ErrorBoundaryWithLogging>
  );
};

export { TranslatedItemBannerInternal };
export default TranslatedItemBanner;
